import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  TrendingUp,
  TrendingDown,
  Activity,
  BarChart3,
  Minus,
} from "lucide-react";
import KPICard from "./ui/KPICard";
import Card from "./ui/Card";

// ─── helpers ────────────────────────────────────────────────────────────────────

function fmt(val, decimals = 2) {
  if (val === null || val === undefined) return "—";
  return Number(val).toFixed(decimals);
}

function pctColor(pct) {
  if (pct > 0) return "text-green-500";
  if (pct < 0) return "text-red-500";
  return "text-[var(--muted)]";
}

function PctBadge({ pct }) {
  if (pct === null || pct === undefined) return <span className="text-[var(--muted)] text-xs">—</span>;
  const Icon = pct > 0 ? TrendingUp : pct < 0 ? TrendingDown : Minus;
  return (
    <span className={`flex items-center gap-1 text-xs font-medium ${pctColor(pct)}`}>
      <Icon size={12} />
      {pct > 0 ? "+" : ""}
      {fmt(pct, 2)}%
    </span>
  );
}

const STANDARD_CARD_CLASS = "bg-[var(--card)] border border-[var(--border)] rounded-xl p-6 transition-all duration-150 hover:shadow-lg";

// ─── Market (EIA) ───────────────────────────────────────────────────────────────

function OilMarketSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get("/oil/market/summary")
      .then((r) => r.data?.data && setData(r.data.data))
      .catch(() => null)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <SectionSkeleton />;
  if (!data) return null;

  const {
    wti,
    brent,
    spreadBrentMinusWti,
    crudeProduction,
    petroleumDemand,
    crudeInventories,
    tradeImports,
    tradeExports,
  } = data;

  const hasTradeFlowData =
    tradeImports?.latest?.value !== null ||
    tradeExports?.latest?.value !== null;

  const netTradeFlow =
    Number.isFinite(tradeExports?.latest?.value) && Number.isFinite(tradeImports?.latest?.value)
      ? tradeExports.latest.value - tradeImports.latest.value
      : null;

  const tradeImportsUnit = tradeImports?.latest?.unit || "";
  const tradeExportsUnit = tradeExports?.latest?.unit || "";
  const sameTradeUnit =
    tradeImportsUnit &&
    tradeExportsUnit &&
    tradeImportsUnit.toLowerCase() === tradeExportsUnit.toLowerCase();
  const netTradeUnit = sameTradeUnit ? tradeExportsUnit : "";

  const hasProductionDemandData =
    (crudeProduction?.latest?.value !== null && crudeProduction?.latest?.value !== undefined) ||
    (petroleumDemand?.latest?.value !== null && petroleumDemand?.latest?.value !== undefined);

  const netProductionMinusDemand =
    Number.isFinite(crudeProduction?.latest?.value) && Number.isFinite(petroleumDemand?.latest?.value)
      ? crudeProduction.latest.value - petroleumDemand.latest.value
      : null;

  function fmtLarge(val) {
    if (val === null || val === undefined || !Number.isFinite(Number(val))) return "—";
    return Number(val).toLocaleString("pt-BR", { maximumFractionDigits: 2 });
  }

  return (
    <section className="mb-10">
      <SectionHeader
        icon={<Activity size={20} />}
        title="Mercado de Petróleo"
        subtitle="Preços spot e indicadores dos EUA/Europa (EIA)"
      />
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <KPICard
          label="WTI (Cushing, EUA)"
          value={wti?.latest?.value ? `$${fmt(wti.latest.value)} /bbl` : "—"}
          change={<PctBadge pct={wti?.dayChangePct} />}
          className={STANDARD_CARD_CLASS}
        />
        <KPICard
          label="Brent (Europa)"
          value={brent?.latest?.value ? `$${fmt(brent.latest.value)} /bbl` : "—"}
          change={<PctBadge pct={brent?.dayChangePct} />}
          className={STANDARD_CARD_CLASS}
        />
        <KPICard
          label="Spread Brent–WTI (Europa - EUA)"
          value={spreadBrentMinusWti !== null ? `$${fmt(spreadBrentMinusWti)} /bbl` : "—"}
          className={STANDARD_CARD_CLASS}
        />
        {hasProductionDemandData && (
          <Card className={`${STANDARD_CARD_CLASS} flex flex-col gap-3`}>
            <p className="text-xs text-[var(--muted)] uppercase tracking-wide">
              Produção x Demanda (EUA, NUS)
            </p>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-[var(--text)]">Produção de Petróleo Bruto</span>
                <div className="text-right">
                  <p className="text-base font-semibold text-[var(--text)]">
                    {crudeProduction?.latest?.value !== null && crudeProduction?.latest?.value !== undefined
                      ? `${fmtLarge(crudeProduction.latest.value)} ${crudeProduction?.latest?.unit || ""}`.trim()
                      : "—"}
                  </p>
                  <div className="flex items-center justify-end gap-2">
                    <PctBadge pct={crudeProduction?.monthChangePct} />
                    {crudeProduction?.latest?.period && (
                      <span className="text-xs text-[var(--muted)]">{crudeProduction.latest.period}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-[var(--text)]">Demanda de Petróleo (EP00)</span>
                <div className="text-right">
                  <p className="text-base font-semibold text-[var(--text)]">
                    {petroleumDemand?.latest?.value !== null && petroleumDemand?.latest?.value !== undefined
                      ? `${fmtLarge(petroleumDemand.latest.value)} ${petroleumDemand?.latest?.unit || ""}`.trim()
                      : "—"}
                  </p>
                  <div className="flex items-center justify-end gap-2">
                    <PctBadge pct={petroleumDemand?.monthChangePct} />
                    {petroleumDemand?.latest?.period && (
                      <span className="text-xs text-[var(--muted)]">{petroleumDemand.latest.period}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-[var(--border)] pt-2 flex items-center justify-between gap-3">
              <span className="text-xs uppercase tracking-wide text-[var(--muted)]">
                Neto (Produção - Demanda)
              </span>
              <span className="text-sm font-semibold text-[var(--text)]">
                {netProductionMinusDemand !== null
                  ? `${fmtLarge(netProductionMinusDemand)} ${crudeProduction?.latest?.unit || petroleumDemand?.latest?.unit || ""}`.trim()
                  : "—"}
              </span>
            </div>
          </Card>
        )}
        <Card className={`${STANDARD_CARD_CLASS} flex flex-col gap-2`}>
          <p className="text-xs text-[var(--muted)] uppercase tracking-wide">
            Estoques Comerciais de Petróleo Bruto (EUA)
          </p>
          <p className="text-2xl font-bold text-[var(--text)]">
            {crudeInventories?.latest?.value !== null && crudeInventories?.latest?.value !== undefined
              ? `${fmtLarge(crudeInventories.latest.value)} ${crudeInventories?.latest?.unit || ""}`.trim()
              : "—"}
          </p>
          <div className="flex items-center gap-2">
            <PctBadge pct={crudeInventories?.monthChangePct} />
            {crudeInventories?.latest?.period && (
              <span className="text-xs text-[var(--muted)]">{crudeInventories.latest.period}</span>
            )}
          </div>
        </Card>

        {hasTradeFlowData && (
          <Card className={`${STANDARD_CARD_CLASS} flex flex-col gap-3`}>
            <p className="text-xs text-[var(--muted)] uppercase tracking-wide">
              Fluxo Comercial (EUA, EPC0)
            </p>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-[var(--text)]">Importações (EUA)</span>
                <div className="text-right">
                  <p className="text-base font-semibold text-[var(--text)]">
                    {tradeImports?.latest?.value !== null && tradeImports?.latest?.value !== undefined
                      ? `${fmtLarge(tradeImports.latest.value)} ${tradeImports?.latest?.unit || ""}`.trim()
                      : "—"}
                  </p>
                  <div className="flex items-center justify-end gap-2">
                    <PctBadge pct={tradeImports?.monthChangePct} />
                    {tradeImports?.latest?.period && (
                      <span className="text-xs text-[var(--muted)]">{tradeImports.latest.period}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-[var(--text)]">Exportações (EUA)</span>
                <div className="text-right">
                  <p className="text-base font-semibold text-[var(--text)]">
                    {tradeExports?.latest?.value !== null && tradeExports?.latest?.value !== undefined
                      ? `${fmtLarge(tradeExports.latest.value)} ${tradeExports?.latest?.unit || ""}`.trim()
                      : "—"}
                  </p>
                  <div className="flex items-center justify-end gap-2">
                    <PctBadge pct={tradeExports?.monthChangePct} />
                    {tradeExports?.latest?.period && (
                      <span className="text-xs text-[var(--muted)]">{tradeExports.latest.period}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-[var(--border)] pt-2 flex items-center justify-between gap-3">
              <span className="text-xs uppercase tracking-wide text-[var(--muted)]">
                Neto (Exportações - Importações)
              </span>
              <span className="text-sm font-semibold text-[var(--text)]">
                {netTradeFlow !== null
                  ? `${fmtLarge(netTradeFlow)}${netTradeUnit ? ` ${netTradeUnit}` : ""}`
                  : "—"}
              </span>
            </div>
          </Card>
        )}
      </div>
      {wti?.latest?.period && (
        <p className="text-xs text-[var(--muted)] mt-2">
          Referência: {wti.latest.period}
        </p>
      )}
    </section>
  );
}

// ─── Macro (FRED) ──────────────────────────────────────────────────────────────

const FRED_LABELS = {
  KGIEA: { label: "Índice de Atividade Econômica Global (Kilian)", unit: "" },
  CPIENGSL: { label: "CPI Energia (EUA)", unit: "" },
  IPCA_BR: { label: "IPCA (Brasil)", unit: "%" },
  SELIC_BR: { label: "Taxa Selic (Brasil)", unit: "% a.d." },
  DEXUSEU: { label: "Câmbio USD/EUR", unit: "" },
  T10Y2Y: { label: "Spread 10y–2y Treasuries", unit: "%" },
  RIGFRTOT: { label: "Contagem de sondas (Baker Hughes)", unit: "" },
  VIX_INDEX: { label: "Índice VIX (CBOE)", unit: "" },
  CPB_Trade_Index: { label: "CPB World Trade Index", unit: "" },
  UNRATE_US: { label: "Taxa de Desemprego (EUA)", unit: "%" },
  UNRATE_BR: { label: "Taxa de Desemprego (Brasil)", unit: "%" },
};

function OilMacroSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get("/oil/macro/summary")
      .then((r) => r.data?.data && setData(r.data.data))
      .catch(() => null)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <SectionSkeleton />;
  if (!data) return null;

  const fxUsdEur = data.DEXUSEU;
  const fxUsdBrl = data.DEXBZUS;
  const cpiUs = data.CPIENGSL;
  const ipcaBr = data.IPCA_BR;
  const kilianIndex = data.KGIEA;
  const cpbTradeIndex = data.CPB_Trade_Index;
  const ratesSpread = data.T10Y2Y;
  const selicBr = data.SELIC_BR;
  const unemploymentUs = data.UNRATE_US;
  const unemploymentBr = data.UNRATE_BR;
  const hasFxData =
    fxUsdEur?.latest?.value !== null ||
    fxUsdBrl?.latest?.value !== null;

  const hasInflationData =
    cpiUs?.latest?.value !== null ||
    ipcaBr?.latest?.value !== null;

  const hasActivityData =
    kilianIndex?.latest?.value !== null ||
    cpbTradeIndex?.latest?.value !== null;

  const hasRatesData =
    (ratesSpread?.latest?.value !== null && ratesSpread?.latest?.value !== undefined) ||
    (selicBr?.latest?.value !== null && selicBr?.latest?.value !== undefined);

  const hasUnemploymentData =
    (unemploymentUs?.latest?.value !== null && unemploymentUs?.latest?.value !== undefined) ||
    (unemploymentBr?.latest?.value !== null && unemploymentBr?.latest?.value !== undefined);

  const brlPerEur =
    Number.isFinite(fxUsdEur?.latest?.value) && Number.isFinite(fxUsdBrl?.latest?.value)
      ? fxUsdEur.latest.value * fxUsdBrl.latest.value
      : null;

  const brlPerEurPct =
    Number.isFinite(fxUsdEur?.changePct) && Number.isFinite(fxUsdBrl?.changePct)
      ? (((1 + fxUsdEur.changePct / 100) * (1 + fxUsdBrl.changePct / 100)) - 1) * 100
      : null;

  const entries = Object.entries(data);

  return (
    <section className="mb-10">
      <SectionHeader
        icon={<BarChart3 size={20} />}
        title="Macroeconomia"
        subtitle="Indicadores globais relacionados ao setor (FRED + BCB + Dallas Fed)"
      />
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {hasInflationData && (
          <Card className={`${STANDARD_CARD_CLASS} flex flex-col gap-3`}>
            <p className="text-xs text-[var(--muted)] uppercase tracking-wide">
              Inflação
            </p>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-[var(--text)]">CPI Energia (EUA)</span>
                <div className="text-right">
                  <p className="text-base font-semibold text-[var(--text)]">
                    {cpiUs?.latest?.value !== null && cpiUs?.latest?.value !== undefined
                      ? fmt(cpiUs.latest.value)
                      : "—"}
                  </p>
                  <div className="flex items-center justify-end gap-2">
                    <PctBadge pct={cpiUs?.changePct} />
                    {cpiUs?.latest?.period && (
                      <span className="text-xs text-[var(--muted)]">{cpiUs.latest.period}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-[var(--text)]">IPCA (Brasil)</span>
                <div className="text-right">
                  <p className="text-base font-semibold text-[var(--text)]">
                    {ipcaBr?.latest?.value !== null && ipcaBr?.latest?.value !== undefined
                      ? `${fmt(ipcaBr.latest.value)} %`
                      : "—"}
                  </p>
                  <div className="flex items-center justify-end gap-2">
                    <PctBadge pct={ipcaBr?.changePct} />
                    {ipcaBr?.latest?.period && (
                      <span className="text-xs text-[var(--muted)]">{ipcaBr.latest.period}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        )}

        {hasFxData && (
          <Card className={`${STANDARD_CARD_CLASS} flex flex-col gap-3`}>
            <p className="text-xs text-[var(--muted)] uppercase tracking-wide">
              Câmbio
            </p>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-[var(--text)]">Real/Dólar (R$/US$)</span>
                <div className="text-right">
                  <p className="text-base font-semibold text-[var(--text)]">
                    {fxUsdBrl?.latest?.value !== null && fxUsdBrl?.latest?.value !== undefined
                      ? fmt(fxUsdBrl.latest.value)
                      : "—"}
                  </p>
                  <div className="flex items-center justify-end gap-2">
                    <PctBadge pct={fxUsdBrl?.changePct} />
                    {fxUsdBrl?.latest?.period && (
                      <span className="text-xs text-[var(--muted)]">{fxUsdBrl.latest.period}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-[var(--text)]">Real/Euro (R$/EUR)</span>
                <div className="text-right">
                  <p className="text-base font-semibold text-[var(--text)]">
                    {brlPerEur !== null ? fmt(brlPerEur) : "—"}
                  </p>
                  <div className="flex items-center justify-end gap-2">
                    <PctBadge pct={brlPerEurPct} />
                    {fxUsdEur?.latest?.period && fxUsdBrl?.latest?.period && (
                      <span className="text-xs text-[var(--muted)]">
                        {fxUsdEur.latest.period > fxUsdBrl.latest.period ? fxUsdBrl.latest.period : fxUsdEur.latest.period}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-[var(--text)]">Câmbio USD/EUR</span>
                <div className="text-right">
                  <p className="text-base font-semibold text-[var(--text)]">
                    {fxUsdEur?.latest?.value !== null && fxUsdEur?.latest?.value !== undefined
                      ? fmt(fxUsdEur.latest.value)
                      : "—"}
                  </p>
                  <div className="flex items-center justify-end gap-2">
                    <PctBadge pct={fxUsdEur?.changePct} />
                    {fxUsdEur?.latest?.period && (
                      <span className="text-xs text-[var(--muted)]">{fxUsdEur.latest.period}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        )}

        {hasActivityData && (
          <Card className={`${STANDARD_CARD_CLASS} flex flex-col gap-3`}>
            <p className="text-xs text-[var(--muted)] uppercase tracking-wide">
              Índices de Atividade Global
            </p>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-[var(--text)]">Kilian (Dallas Fed)</span>
                <div className="text-right">
                  <p className="text-base font-semibold text-[var(--text)]">
                    {kilianIndex?.latest?.value !== null && kilianIndex?.latest?.value !== undefined
                      ? fmt(kilianIndex.latest.value)
                      : "—"}
                  </p>
                  <div className="flex items-center justify-end gap-2">
                    <PctBadge pct={kilianIndex?.changePct} />
                    {kilianIndex?.latest?.period && (
                      <span className="text-xs text-[var(--muted)]">{kilianIndex.latest.period}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-[var(--text)]">CPB World Trade Index</span>
                <div className="text-right">
                  <p className="text-base font-semibold text-[var(--text)]">
                    {cpbTradeIndex?.latest?.value !== null && cpbTradeIndex?.latest?.value !== undefined
                      ? Number(cpbTradeIndex.latest.value).toLocaleString("en-US", { maximumFractionDigits: 1 })
                      : "—"}
                  </p>
                  <div className="flex items-center justify-end gap-2">
                    <PctBadge pct={cpbTradeIndex?.changePct} />
                    {cpbTradeIndex?.latest?.period && (
                      <span className="text-xs text-[var(--muted)]">{cpbTradeIndex.latest.period}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        )}

        {hasRatesData && (
          <Card className={`${STANDARD_CARD_CLASS} flex flex-col gap-3`}>
            <p className="text-xs text-[var(--muted)] uppercase tracking-wide">
              Juros
            </p>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-[var(--text)]">Spread Treasury 10a - 2a (EUA)</span>
                <div className="text-right">
                  <p className="text-base font-semibold text-[var(--text)]">
                    {ratesSpread?.latest?.value !== null && ratesSpread?.latest?.value !== undefined
                      ? `${fmt(ratesSpread.latest.value)} %`
                      : "—"}
                  </p>
                  <div className="flex items-center justify-end gap-2">
                    <PctBadge pct={ratesSpread?.changePct} />
                    {ratesSpread?.latest?.period && (
                      <span className="text-xs text-[var(--muted)]">{ratesSpread.latest.period}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-[var(--text)]">Taxa Selic Over (Brasil)</span>
                <div className="text-right">
                  <p className="text-base font-semibold text-[var(--text)]">
                    {selicBr?.latest?.value !== null && selicBr?.latest?.value !== undefined
                      ? `${fmt(selicBr.latest.value)} % a.d.`
                      : "—"}
                  </p>
                  <div className="flex items-center justify-end gap-2">
                    <PctBadge pct={selicBr?.changePct} />
                    {selicBr?.latest?.period && (
                      <span className="text-xs text-[var(--muted)]">{selicBr.latest.period}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        )}

        {hasUnemploymentData && (
          <Card className={`${STANDARD_CARD_CLASS} flex flex-col gap-3`}>
            <p className="text-xs text-[var(--muted)] uppercase tracking-wide">
              Desemprego
            </p>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-[var(--text)]">Taxa de Desemprego (EUA)</span>
                <div className="text-right">
                  <p className="text-base font-semibold text-[var(--text)]">
                    {unemploymentUs?.latest?.value !== null && unemploymentUs?.latest?.value !== undefined
                      ? `${fmt(unemploymentUs.latest.value)} %`
                      : "—"}
                  </p>
                  <div className="flex items-center justify-end gap-2">
                    <PctBadge pct={unemploymentUs?.changePct} />
                    {unemploymentUs?.latest?.period && (
                      <span className="text-xs text-[var(--muted)]">{unemploymentUs.latest.period}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-[var(--text)]">Taxa de Desemprego (Brasil)</span>
                <div className="text-right">
                  <p className="text-base font-semibold text-[var(--text)]">
                    {unemploymentBr?.latest?.value !== null && unemploymentBr?.latest?.value !== undefined
                      ? `${fmt(unemploymentBr.latest.value)} %`
                      : "—"}
                  </p>
                  <div className="flex items-center justify-end gap-2">
                    <PctBadge pct={unemploymentBr?.changePct} />
                    {unemploymentBr?.latest?.period && (
                      <span className="text-xs text-[var(--muted)]">{unemploymentBr.latest.period}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        )}

        {entries
          .filter(([seriesId, row]) => (
            seriesId !== "DEXUSEU" &&
            seriesId !== "DEXBZUS" &&
            seriesId !== "CPIENGSL" &&
            seriesId !== "IPCA_BR" &&
            seriesId !== "KGIEA" &&
            seriesId !== "CPB_Trade_Index" &&
            seriesId !== "T10Y2Y" &&
            seriesId !== "SELIC_BR" &&
            seriesId !== "UNRATE_US" &&
            seriesId !== "UNRATE_BR" &&
            row?.latest?.value !== null &&
            row?.latest?.value !== undefined
          ))
          .map(([seriesId, row]) => {
          const meta = FRED_LABELS[seriesId] || { label: seriesId, unit: "" };
          return (
            <Card key={seriesId} className={`${STANDARD_CARD_CLASS} flex flex-col gap-1`}>
              <p className="text-xs text-[var(--muted)] uppercase tracking-wide">
                {meta.label}
              </p>
              <p className="text-2xl font-bold text-[var(--text)]">
                {row?.latest?.value !== null && row?.latest?.value !== undefined
                  ? `${fmt(row.latest.value)}${meta.unit ? " " + meta.unit : ""}`
                  : "—"}
              </p>
              <div className="flex items-center gap-2">
                <PctBadge pct={row?.changePct} />
                {row?.latest?.period && (
                  <span className="text-xs text-[var(--muted)]">{row.latest.period}</span>
                )}
              </div>
            </Card>
          );
          })}
      </div>
    </section>
  );
}

// ─── Shared UI ─────────────────────────────────────────────────────────────────

function SectionHeader({ icon, title, subtitle }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <span className="text-[var(--accent)]">{icon}</span>
      <div>
        <h2 className="text-xl font-bold text-[var(--text)]">{title}</h2>
        {subtitle && <p className="text-sm text-[var(--muted)]">{subtitle}</p>}
      </div>
    </div>
  );
}

function SectionSkeleton() {
  return (
    <div className="grid grid-cols-3 gap-4 mb-10 animate-pulse">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-24 rounded-xl bg-[var(--border)]" />
      ))}
    </div>
  );
}

// ─── Export ────────────────────────────────────────────────────────────────────

export { OilMarketSection, OilMacroSection };
